import {
    Button,
    createTableColumn,
    TableCellLayout,
    Tooltip,
} from '@fluentui/react-components';
import { TFunction } from 'i18next';
import {
    AirplaneRegular,
    AirplaneTakeOffRegular,
    bundleIcon,
    CalendarCheckmarkRegular,
    Chat24Filled,
    Chat24Regular,
    NumberRow16Regular,
    NumberSymbolSquareRegular,
    PeopleRegular,
    RibbonStarRegular,
} from '@fluentui/react-icons';
import { formatFlightTeamTime } from '../../../utils/formatters';
import { BaggageIcon } from '../../Icons/BaggageIcon';
import CustomerIcon from '../../Icons/CustomerIcon';
import {
    CustomerFlightTeam,
    CustomerWithStatuses,
} from '../../../model/customerWithUpdates';
import { CustomerUpdatesType } from '../../../store/features/customerUpdates/customerUpdates.types';
import CustomerUpdatesTableAction from './CustomerUpdatesTableAction/CustomerUpdatesTableAction';
import MailIcon from '../../Icons/MailIcon';

const SendMessageIconBundle = bundleIcon(Chat24Filled, Chat24Regular);

export const customerUpdatesTableColumnIds = {
    displayName: 'displayName',
    flight: 'flight',
    airport: 'airport',
    departureTime: 'departureTime',
    arrivalTime: 'arrivalTime',
    seatNumber: 'seatNumber',
    loyaltyLevel: 'loyaltyLevel',
    actions: 'actions',
} as const;

export type CustomerUpdatesTableColumnId =
    (typeof customerUpdatesTableColumnIds)[keyof typeof customerUpdatesTableColumnIds];

export const getCustomerUpdatesTableColumnsConfig = (
    t: TFunction<'translation', undefined>,
    onPassengerSelected: (
        passenger: CustomerWithStatuses,
        updatesType: CustomerUpdatesType
    ) => void,
    opendSendMessageToTeamDialog: (
        item: CustomerFlightTeam,
        useSingleKey: boolean
    ) => void
) => {
    return [
        createTableColumn<CustomerWithStatuses>({
            columnId: customerUpdatesTableColumnIds.displayName,
            renderHeaderCell: () => {
                return (
                    <TableCellLayout media={<PeopleRegular />}>
                        {t('TableHeader_Customer_DisplayName')}
                    </TableCellLayout>
                );
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>{`${item.customer.firstName} ${item.customer.lastName}`}</TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerWithStatuses>({
            columnId: customerUpdatesTableColumnIds.flight,
            renderHeaderCell: () => {
                return (
                    <>
                        <NumberRow16Regular />
                        {t('TableHeader_Flight')}
                    </>
                );
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        {item.flightTeam.iataFlightIdentifier}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerWithStatuses>({
            columnId: customerUpdatesTableColumnIds.airport,
            renderHeaderCell: () => {
                return (
                    <>
                        <AirplaneRegular />
                        {t('TableHeader_Airport')}
                    </>
                );
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>{`${item.flightTeam.departureAirportIata} - ${item.flightTeam.arrivalAirportIata}`}</TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerWithStatuses>({
            columnId: customerUpdatesTableColumnIds.departureTime,
            renderHeaderCell: () => {
                return (
                    <>
                        <AirplaneTakeOffRegular />
                        {t('TableHeader_DepartureTime')}
                    </>
                );
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        {formatFlightTeamTime(
                            item.flightTeam.scheduledDepartureTime,
                            item.flightTeam.scheduledDepartureTimeUtc
                        )}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerWithStatuses>({
            columnId: customerUpdatesTableColumnIds.arrivalTime,
            renderHeaderCell: () => {
                return (
                    <>
                        <CalendarCheckmarkRegular />
                        {t('TableHeader_ArrivalTime')}
                    </>
                );
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        {formatFlightTeamTime(
                            item.flightTeam.scheduledArrivalTime,
                            item.flightTeam.scheduledArrivalTimeUtc
                        )}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerWithStatuses>({
            columnId: customerUpdatesTableColumnIds.seatNumber,
            renderHeaderCell: () => {
                return (
                    <TableCellLayout media={<NumberSymbolSquareRegular />}>
                        {t('TableHeader_Passenger_SeatNumber')}
                    </TableCellLayout>
                );
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        {item.customer.seatNumber}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerWithStatuses>({
            columnId: customerUpdatesTableColumnIds.loyaltyLevel,
            renderHeaderCell: () => {
                return (
                    <TableCellLayout media={<RibbonStarRegular />}>
                        {t('TableHeader_Passenger_LoyaltyLevel')}
                    </TableCellLayout>
                );
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        {item.customer.frequentFlyerTier}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerWithStatuses>({
            columnId: customerUpdatesTableColumnIds.actions,
            renderHeaderCell: () => {
                return <></>;
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        <CustomerUpdatesTableAction
                            status={item.mishandledBaggage?.status}
                            iconFactory={(props) => <BaggageIcon {...props} />}
                            onClick={() =>
                                onPassengerSelected(item, 'MishandledBaggage')
                            }
                        />
                        <CustomerUpdatesTableAction
                            status={item.lastRebookedFlight?.status}
                            iconFactory={(props) => <CustomerIcon {...props} />}
                            onClick={() =>
                                onPassengerSelected(
                                    item,
                                    'MisconnectedCustomers'
                                )
                            }
                        />
                        <CustomerUpdatesTableAction
                            status={item.lastMessage?.status}
                            iconFactory={(props) => <MailIcon {...props} />}
                            onClick={() =>
                                onPassengerSelected(item, 'CustomerMessages')
                            }
                        />
                        <Tooltip
                            content={t('Button_SendMessageToTeam_Tooltip')!}
                            relationship="label"
                            withArrow
                        >
                            <Button
                                appearance="transparent"
                                icon={<SendMessageIconBundle />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    opendSendMessageToTeamDialog(
                                        item.flightTeam!,
                                        true
                                    );
                                }}
                            />
                        </Tooltip>
                    </TableCellLayout>
                );
            },
        }),
    ];
};
